import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "@/constants/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const linkClass = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition-colors duration-300 ${
      isActive ? "text-[#86EFAC]" : "text-[#F6F4EE]/70 hover:text-[#F6F4EE]"
    }`;

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#0F291E]/85 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
        <Link to="/" data-testid="header-logo-link" className="group flex items-center gap-3">
          <img
            src="/logo-official.png"
            alt="Barnala Labour Contracting logo"
            data-testid="header-logo-image"
            className="h-11 w-11 rounded-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <span className="leading-tight">
            <span className="block font-display text-lg font-bold tracking-tight">BARNALA</span>
            <span className="block font-mono text-[10px] font-medium uppercase tracking-[0.3em] text-[#86EFAC]/80">
              Labour Contracting
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} data-testid={link.testId} className={linkClass}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PHONE_TEL}
            data-testid="header-call-button"
            className="hidden items-center gap-2 rounded-full bg-[#22C55E] px-5 py-2.5 text-sm font-semibold text-[#0F291E] transition-transform duration-300 hover:scale-105 sm:inline-flex"
          >
            <Phone className="h-4 w-4" />
            {PHONE_DISPLAY}
          </a>
          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#F6F4EE] lg:hidden"
            aria-label="Toggle navigation menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname + "-mobile"}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#0F291E]/95 backdrop-blur-md lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-${link.testId}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                      isActive ? "bg-[#163B2C] text-[#86EFAC]" : "text-[#F6F4EE]/80 hover:bg-[#163B2C]/60"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <a
                href={PHONE_TEL}
                data-testid="mobile-call-button"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#22C55E] px-5 py-3 text-sm font-semibold text-[#0F291E]"
              >
                <Phone className="h-4 w-4" />
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
